import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  GetMobileCheckout,
  PayWithPhonePe,
  PayWithStripe,
} from "../services/api_mobile_checkout";

const Spinner = ({ className = "" }) => (
  <div className={`spinner-border ${className}`} role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const MobileCheckout = () => {
  const [searchParams] = useSearchParams();
  const checkoutId = searchParams.get("id");

  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingGateway, setProcessingGateway] = useState(null);

  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const formatCurrency = (amount, currency = "INR") => {
    const num = Number(amount) || 0;
    if (currency === "INR") return `₹${num.toLocaleString("en-IN")}`;
    if (currency === "USD") return `$${num.toLocaleString("en-US")}`;
    return `${currency} ${num.toLocaleString()}`;
  };

  useEffect(() => {
    if (!checkoutId) {
      setError("Invalid checkout link");
      setLoading(false);
      return;
    }

    if (isLocalhost) {
      const mockCheckout = {
        checkoutId: "b4bacab0-55a8-45bc-9d4e-4a22caed3a7f",
        currency: "INR",
        items: [
          {
            Id: "course-101",
            expireInDays: 365,
            price: 1499.0,
            image: "https://skillbakery.com/images/course1.jpg",
            title: "ASP.NET Core Mastery",
            type: "download",
            currency: "INR",
          },
        ],
        totalAmount: 1499.0,
        status: "Pending",
        isExpired: false,
        createdOn: "2026-06-25T14:51:56.1111713",
        expiresOn: "2026-07-25T15:21:56.1112325",
      };
      setTimeout(() => {
        setCheckout(mockCheckout);
        setLoading(false);
      }, 300);
      return;
    }

    GetMobileCheckout(checkoutId, {
      onSuccess: (data) => {
        setCheckout(data);
        setLoading(false);
      },
      onError: (err) => {
        setError(err);
        setLoading(false);
      },
    });
  }, [checkoutId, isLocalhost]);

  const handlePay = (gateway) => {
    setProcessingGateway(gateway);
    
    const payFn = gateway === "phonepe" ? PayWithPhonePe : PayWithStripe;
    payFn(checkoutId, {
      onSuccess: (data) => {
        if (data.url) window.location.href = data.url;
      },
      onError: (err) => {
        setError(err);
        setProcessingGateway(null);
      },
    });
  };

  const handleCancel = async () => {
    if (checkoutId) {
      try {
        await fetch(`/api/mobilecheckout/${checkoutId}/cancel`, {
          method: "POST",
        });
      } catch (_) {}
    }
    window.history.back();
  };

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <div
        className="min-vh-100 position-relative overflow-hidden"
        style={{ background: "#0a0a0f" }}
      >
        <img
          src="./images/giphy-new.gif"
          alt="background"
          className="position-absolute w-100 h-100 object-fit-cover"
          style={{ opacity: 0.5 }}
        />
        <div
          className="position-absolute w-100 h-100"
          style={{ background: "rgba(0,0,0,0.6)" }}
        ></div>

        <div className="position-relative d-flex align-items-center justify-content-center min-vh-100 px-3">
          <div
            className="card shadow-lg border-0"
            style={{ maxWidth: "420px", borderRadius: "24px" }}
          >
            <div className="card-body p-5 text-center">
              <Spinner
                className="text-primary mb-4"
                style={{ width: "3rem", height: "3rem" }}
              />
              <h2 className="fw-bold mb-3">Preparing Checkout</h2>
              <p className="text-muted">
                Please wait while we securely prepare your payment session.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== ERROR STATE ====================
  if (error || !checkout) {
    return (
      <div
        className="min-vh-100 position-relative overflow-hidden"
        style={{ background: "#0a0a0f" }}
      >
        <img
          src="./images/giphy-new.gif"
          alt="background"
          className="position-absolute w-100 h-100 object-fit-cover"
          style={{ opacity: 0.5 }}
        />
        <div
          className="position-absolute w-100 h-100"
          style={{ background: "rgba(0,0,0,0.6)" }}
        ></div>

        <div className="position-relative d-flex align-items-center justify-content-center min-vh-100 px-3">
          <div
            className="card shadow-lg border-0 text-center"
            style={{ maxWidth: "420px", borderRadius: "24px" }}
          >
            <div className="card-body p-5">
              <div
                className="bg-danger bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                style={{ width: "80px", height: "80px" }}
              >
                <i className="bi bi-exclamation-triangle-fill text-danger fs-1"></i>
              </div>
              <h2 className="fw-bold mb-3">Unable to Load Checkout</h2>
              <p className="text-muted">
                {error ||
                  "Something went wrong while loading this checkout session."}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="btn btn-primary btn-lg w-100 mt-4"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // ==================== ADD THIS HELPER (near top of component) ====================
  const parseServerDate = (dateStr) => {
    if (!dateStr) return null;
    // Force UTC parsing (backend returns UTC without 'Z')
    const normalized =
      dateStr.endsWith("Z") ||
      dateStr.includes("+") ||
      dateStr.includes("-", 10)
        ? dateStr
        : dateStr + "Z";
    return new Date(normalized);
  };

  // ==================== REPLACE YOUR isExpired LINE ====================
  const checkoutExpiresOn = parseServerDate(checkout.expiresOn);
  const nowWithBuffer = new Date(Date.now() + 60000); // 60s buffer

  const isExpired =
    checkout.isExpired === true || // trust backend flag first
    (checkoutExpiresOn && checkoutExpiresOn < nowWithBuffer);
  const isINR = checkout.currency === "INR";
  const item = checkout.items?.[0];

  // ==================== MAIN CHECKOUT ====================
  return (
    <div
      className="min-vh-100 position-relative overflow-hidden"
      style={{ background: "#0a0a0f" }}
    >
      {/* Background */}
      <img
        src="./images/giphy-new.gif"
        alt="background"
        className="position-absolute w-100 h-100 object-fit-cover"
        style={{ opacity: 0.45 }}
      />
      <div
        className="position-absolute w-100 h-100"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.65), rgba(15,23,42,0.75))",
        }}
      ></div>

      <div className="position-relative py-5 px-3">
        <div className="container" style={{ maxWidth: "620px" }}>
          {/* Trust Badge */}
          <div className="d-flex justify-content-center mb-4">
            <div
              className="d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill border"
              style={{
                background: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(12px)",
              }}
            >
              <div
                className="bg-success rounded-circle"
                style={{ width: "10px", height: "10px" }}
              ></div>
              <span className="text-white fw-semibold small">
                Secure Checkout • 256-bit SSL
              </span>
            </div>
          </div>

          {/* Heading */}
          <div className="text-center mb-5">
            <h1 className="display-5 fw-bold text-white mb-3">
              Complete Your Purchase
            </h1>
            <p className="text-white-50 mx-auto" style={{ maxWidth: "420px" }}>
              You're just one step away from getting instant access to your
              purchase.
            </p>
          </div>

          {/* Main Card */}
          <div
            className="card shadow-lg border-0"
            style={{ borderRadius: "24px", overflow: "hidden" }}
          >
            {/* Accent Bar */}
            <div
              style={{
                height: "6px",
                background:
                  "linear-gradient(to right, #6366f1, #a855f7, #ec4899)",
              }}
            ></div>

            <div className="card-body p-4 p-md-5">
              {/* Order Summary */}
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                  <div className="text-primary fw-bold small text-uppercase tracking-wide">
                    Order Summary
                  </div>
                  <h4 className="fw-bold mt-1 mb-0">Review Your Order</h4>
                </div>
                <span className="badge bg-success-subtle text-success px-3 py-2 rounded-pill">
                  <i className="bi bi-shield-check me-1"></i> Secure
                </span>
              </div>

              {/* Product Card */}
              <div className="card bg-light border mb-4">
                <div className="card-body p-3">
                  <div className="d-flex gap-3">
                    <div
                      style={{ width: "90px", height: "90px", flexShrink: 0 }}
                      className="rounded-3 overflow-hidden border bg-white"
                    >
                      {item?.image ? (
                        <img
                          src={item.image}
                          alt={item.title}
                          className="w-100 h-100 object-fit-cover"
                        />
                      ) : (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-secondary bg-opacity-10">
                          <i className="bi bi-image fs-2 text-muted"></i>
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="fw-bold mb-2">{item?.title}</h5>
                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge bg-primary bg-opacity-10 text-primary">
                          Lifetime Access
                        </span>
                        <span className="badge bg-success bg-opacity-10 text-success">
                          Instant Access
                        </span>
                        <span className="badge bg-warning bg-opacity-10 text-warning">
                          Premium Course
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="bg-light rounded-4 p-4 mb-4">
                <div className="d-flex justify-content-between mb-3">
                  <span className="text-muted">Course Price</span>
                  <span className="fw-semibold">
                    {formatCurrency(item?.price, checkout.currency)}
                  </span>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <span className="text-muted">Taxes</span>
                  <span className="fw-semibold text-success">Included</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between align-items-end">
                  <div>
                    <div className="text-muted small fw-bold">
                      TOTAL PAYABLE
                    </div>
                    <div className="fs-2 fw-bold">
                      {formatCurrency(checkout.totalAmount, checkout.currency)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Section */}
              {!isExpired && checkout.status === "Pending" && (
                <>
                  <h5 className="fw-bold mb-3">Choose Payment Method</h5>
                  <p className="text-muted mb-4">
                    Select your preferred option. You will be redirected to a
                    secure gateway.
                  </p>

                  {/* Security Note */}
                  <div
                    className="alert alert-primary d-flex gap-3 mb-4"
                    role="alert"
                  >
                    <i className="bi bi-lock-fill fs-4"></i>
                    <div>
                      <strong>Safe &amp; Secure Payment</strong>
                      <br />
                      <small>
                        Your payment is encrypted with industry-standard SSL. We
                        never store your card details.
                      </small>
                    </div>
                  </div>

                  {/* Payment Buttons */}
                  {isINR ? (
                    <button
                      onClick={() => handlePay("phonepe")}
                      disabled={!!processingGateway}
                      className="btn w-100 py-3 d-flex align-items-center justify-content-between px-4 mb-3"
                      style={{
                        backgroundColor: "#5F259F",
                        color: "white",
                        borderRadius: "16px",
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="bg-white rounded-3 d-flex align-items-center justify-content-center"
                          style={{ width: "48px", height: "48px" }}
                        >
                          <span
                            className="fw-bold fs-4"
                            style={{ color: "#5F259F" }}
                          >
                            P
                          </span>
                        </div>
                        <div className="text-start">
                          <div className="fw-bold">Pay with PhonePe</div>
                          <small className="opacity-75">
                            UPI • Cards • Wallet • Net Banking
                          </small>
                        </div>
                      </div>
                      <span className="fs-4">→</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePay("stripe")}
                      disabled={!!processingGateway}
                      className="btn w-100 py-3 d-flex align-items-center justify-content-between px-4"
                      style={{
                        backgroundColor: "#635BFF",
                        color: "white",
                        borderRadius: "16px",
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="bg-white rounded-3 d-flex align-items-center justify-content-center"
                          style={{ width: "48px", height: "48px" }}
                        >
                          <span
                            className="fw-bold fs-4"
                            style={{ color: "#635BFF" }}
                          >
                            S
                          </span>
                        </div>
                        <div className="text-start">
                          <div className="fw-bold">Pay with Stripe</div>
                          <small className="opacity-75">
                            Credit / Debit Card
                          </small>
                        </div>
                      </div>
                      <span className="fs-4">→</span>
                    </button>
                  )}

                  {processingGateway && (
                    <div className="alert alert-warning d-flex align-items-center gap-3 mt-3">
                      <Spinner />
                      <span>Redirecting to secure payment page...</span>
                    </div>
                  )}
                </>
              )}

              {/* Expired / Completed State */}
              {(isExpired || checkout.status !== "Pending") && (
                <div className="text-center py-4">
                  <div
                    className={`d-inline-flex align-items-center justify-content-center rounded-circle mb-4 ${checkout.status === "Completed" ? "bg-success bg-opacity-10" : "bg-warning bg-opacity-10"}`}
                    style={{ width: "80px", height: "80px" }}
                  >
                    {checkout.status === "Completed" ? (
                      <i className="bi bi-check-circle-fill text-success fs-1"></i>
                    ) : (
                      <i className="bi bi-clock-history text-warning fs-1"></i>
                    )}
                  </div>
                  <h4 className="fw-bold">
                    {checkout.status === "Completed"
                      ? "Payment Successful"
                      : "Checkout Expired"}
                  </h4>
                  <p className="text-muted mt-2">
                    {checkout.status === "Completed"
                      ? "Your payment was successful. You can safely close this page."
                      : "This checkout link has expired. Please request a new one."}
                  </p>
                  <button
                    onClick={handleCancel}
                    className="btn btn-outline-secondary mt-3 px-5"
                  >
                    Return
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-4 text-white-50 small">
            <div className="d-flex justify-content-center gap-4 mb-2">
              <span>🔒 SSL Secured</span>
              <span>Instant Access</span>
              <span>Trusted Payments</span>
            </div>
            <div>SkillBakery • Powered by PhonePe &amp; Stripe</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileCheckout;
